// Class which exposes a number of functions which
// we use to manage the state of selected Groups.
export default class SupplierGroupStateManager {
  constructor(state, updateState) {
    this.state = state;
    this.updateState = updateState;
  }

  // Updates the state with a selected or deselected suggestion.
  //
  // Example:
  //
  // this.groupManager.handleChange('select', suggestion);
  // this.groupManager.handleChange('deselect', suggestion);
  //
  handleChange(action, suggestion) {
    switch (action) {
      case 'select':
        switch (suggestion.suggestionType) {
          case 'group':
            this._handleSelectGroup(suggestion);
            break;
          case 'supplier':
            this._handleSelectSubsidiary(suggestion);
            break;
        }
        break;
      case 'bulkSelect':
        this._handleSelectSubsidiaries(suggestion);
        break;
      case 'deselect':
        switch (suggestion.suggestionType) {
          case 'group':
            this._handleDeselectGroup(suggestion);
            break;
          case 'supplier':
            this._handleDeselectSubsidiary(suggestion);
            break;
        }
        break;
    }
  }

  // Turns the Group state into a list of suggestionIds.
  // If a group is selected, we want to return all the ids
  // for the associated subsidiaries and the group itself.
  // If a subsidiary is selected, only want to return the
  // id for that selected subsidiary.
  resultsToIgnore() {
    const ignore = [];
    Object.values(this.state).forEach(group => {
      if (group.selected) {
        group.suppliers.forEach(supplier => ignore.push(supplier.suggestionId));
        ignore.push(group.suggestionId);
      } else {
        group.selectedSuppliers.forEach(supplier => ignore.push(supplier.suggestionId));
      }
    });
    return ignore;
  }

  // Returns the groups sorted by name.
  getGroups() {
    const groups = Object.values(this.state);
    return this._sortByName(groups);
  }

  // Selecting a group updates it if it already exists
  // of crate
  _handleSelectGroup(suggestedGroup) {
    const addGroup = groups => {
      const groupName = suggestedGroup.name;

      // create the group if it doesn't exist
      if (!groups[groupName]) {
        groups[groupName] = {
          suggestionType: 'group',
          id: suggestedGroup.id,
          name: suggestedGroup.name,
          suggestionId: suggestedGroup.suggestionId
        };
      }

      // mark the group as selected and set the suppliers from the suggestion
      groups[groupName].selected = true;
      groups[groupName].selectedSuppliers = [];
      groups[groupName].suppliers = suggestedGroup.suppliers;
      return groups;
    };

    this.updateState({ group: { $apply: addGroup } });
  }

  // Deselecting a group removes it from the state object.
  _handleDeselectGroup(group) {
    const removeGroup = groups => {
      // TODO: Refactor this class to not use a hash for state, see 
      // <https://typescript-eslint.io/rules/no-dynamic-delete/>
      /* eslint-disable-next-line @typescript-eslint/no-dynamic-delete */
      delete groups[group.name];
      return groups;
    };

    this.updateState({ group: { $apply: removeGroup } });
  }

  // Selecting a subsidiary adds it to a groups selectedSuppliers list.
  _handleSelectSubsidiary(suggestedSupplier) {
    // To keep things dry I'm re-using the bulk `select` subsidiaries function.
    this._handleSelectSubsidiaries([suggestedSupplier]);
  }

  // Adds the subsidiaries to the correct group's selectedSuppliers list.
  _handleSelectSubsidiaries(suggestedSuppliers) {
    const addSubsidiaries = groups => {
      suggestedSuppliers.forEach(suggestedSupplier => {
        const relatedGroup = groups[suggestedSupplier.groupName];

        // if the group exists add the subsidiary to the group's selectedSuppliers.
        // NOTE: we are storing the selectedSuppliers in alphabetical order to
        //       stop them jumping around during selection and deselection.
        if (relatedGroup) {
          const unsortedSelectedSups = [...relatedGroup.selectedSuppliers, suggestedSupplier];
          relatedGroup.selectedSuppliers = this._sortByName(unsortedSelectedSups);
          return;
        }

        // if the group doesn't exist create it
        groups[suggestedSupplier.groupName] = {
          suggestionType: 'group',
          id: suggestedSupplier.groupId,
          name: suggestedSupplier.groupName,
          suggestionId: `supplierGroup-${suggestedSupplier.groupId}`,
          selected: false,
          selectedSuppliers: [suggestedSupplier],
          suppliers: []
        };
      });

      return groups;
    };

    this.updateState({ group: { $apply: addSubsidiaries } });
  }

  // Deselecting a subsidiary removes it from the groups selectedSuppliers list.
  _handleDeselectSubsidiary(selectedSupplier) {
    const removeSupplier = groups => {
      const relatedGroup = groups[selectedSupplier.groupName];

      // If the Group is changing from wholly selected to a selection of subsidiaries
      if (relatedGroup.selected) {
        relatedGroup.selected = false;
        // Copy the group's suppliers to the selected suppliers
        relatedGroup.selectedSuppliers = relatedGroup.suppliers;
      }

      // and then filter away the deselected supplier.
      const remainingSuppliers = relatedGroup.selectedSuppliers.filter(supplier => supplier.id !== selectedSupplier.id);
      if (remainingSuppliers.length === 0) {
        // TODO: Refactor this class to not use a hash for state, see 
        // <https://typescript-eslint.io/rules/no-dynamic-delete/>
        /* eslint-disable-next-line @typescript-eslint/no-dynamic-delete */
        delete groups[selectedSupplier.groupName];
      } else {
        relatedGroup.selectedSuppliers = remainingSuppliers;
      }

      return groups;
    };

    this.updateState({ group: { $apply: removeSupplier } });
  }

  // sort a list of suppliers or groups by name
  _sortByName(suggestions) {
    return suggestions.sort((a, b) => {
      if (a.name < b.name) { return -1; }
      if (a.name > b.name) { return 1; }
      return 0;
    });
  }
}
